import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion'
import { 
  Sparkles, Brain, Zap, Shield, Globe, Code, Terminal,
  Lock, Eye, EyeOff, X, Rocket, TrendingUp, Users, 
  Clock, CheckCircle, Star, ArrowRight, Cpu, Bot, Network
} from 'lucide-react'
import { AuthService } from './services/auth'
import { Dashboard } from './components/Dashboard'
import './styles/interstellar.css'

// Interstellar Background Component
const InterstellarBackground = () => {
  return (
    <div className="interstellar-background">
      <div className="aurora" />
      <div className="nebula" />
      <div className="stars" />
      <div className="cosmic-dust" />
      <div className="shooting-star" />
      <div className="shooting-star" />
      <div className="shooting-star" />
      <div className="sacred-geometry" />
    </div>
  )
}

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2 }) => {
  const [count, setCount] = useState(0)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  useEffect(() => {
    if (isInView) {
      let startTime
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp
        const progress = (timestamp - startTime) / (duration * 1000)
        
        if (progress < 1) {
          setCount(Math.floor(end * progress))
          requestAnimationFrame(animate)
        } else {
          setCount(end)
        }
      }
      requestAnimationFrame(animate)
    }
  }, [isInView, end, duration])

  return <span ref={ref} className="counter">{count.toLocaleString()}</span>
}

// Auth Modal Component
const AuthModal = ({ isOpen, onClose, onSuccess, queryText }) => {
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (isLogin) {
        await AuthService.login(email, password)
      } else {
        await AuthService.register(email, password)
        await AuthService.login(email, password)
      }
      
      onSuccess(queryText)
      onClose()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-6 modal-blur"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="glass-card p-10 max-w-md w-full relative gleam"
          initial={{ scale: 0.8, rotateX: -20, y: 100 }}
          animate={{ scale: 1, rotateX: 0, y: 0 }}
          exit={{ scale: 0.8, rotateX: 20, y: -100 }}
          transition={{ type: "spring", damping: 20, stiffness: 200 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-white/60 hover:text-white transition-all duration-300 hover:rotate-90"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-block mb-4"
            >
              <Sparkles className="h-16 w-16 text-purple-400 mx-auto" />
            </motion.div>
            <h2 className="text-3xl font-black text-white mb-2">
              {isLogin ? 'Welcome Back' : 'Join Agentcy1'}
            </h2>
            <p className="text-white/60">
              {isLogin ? 'Sign in to continue your journey' : 'Start your AI-powered adventure'}
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-2xl text-red-300 text-sm"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/40 focus:outline-none focus:border-purple-400/50 transition-all duration-300"
                required
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/40 focus:outline-none focus:border-purple-400/50 transition-all duration-300"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-4 rounded-2xl hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <Cpu className="h-5 w-5 mr-2 animate-spin" />
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Rocket className="h-5 w-5 mr-2" />
                  {isLogin ? 'Sign In' : 'Get Started'}
                </span>
              )}
            </motion.button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-white/60 hover:text-white text-sm transition-colors duration-300"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// Feature Box Component
const FeatureBox = ({ icon: Icon, title, description, delay = 0 }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="bento-box gleam group"
    >
      <div className="inline-flex p-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
        <Icon className="h-8 w-8 text-purple-300" />
      </div>
      <h3 className="text-2xl font-bold text-white mb-3">{title}</h3>
      <p className="text-white/60 leading-relaxed">{description}</p>
    </motion.div>
  )
}

// Main App Component
const App = () => {
  const [query, setQuery] = useState('')
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingProgress, setProcessingProgress] = useState(0)
  const [user, setUser] = useState(null)
  const [pendingQuery, setPendingQuery] = useState('')

  const { scrollY } = useScroll()
  const heroY = useTransform(scrollY, [0, 500], [0, 150])

  useEffect(() => {
    const token = localStorage.getItem('authToken')
    if (token) {
      setUser({ email: 'user@example.com' })
    }
  }, [])

  const handleQuerySubmit = (e) => {
    e.preventDefault()
    if (!query.trim()) return

    setPendingQuery(query)
    
    // Simulate processing
    setIsProcessing(true)
    setProcessingProgress(0)

    const interval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval)
          // Show auth modal after reaching 90%
          setTimeout(() => {
            setIsProcessing(false)
            setShowAuthModal(true)
          }, 500)
          return 90
        }
        return prev + 10
      })
    }, 200)
  }

  const handleAuthSuccess = async (queryText) => {
    // Get user data from token
    const token = localStorage.getItem('authToken')
    try {
      const response = await fetch('https://agentcy-workers.pounds1.workers.dev/api/tokens/balance', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      const data = await response.json()
      if (data.success) {
        setUser({ email: localStorage.getItem('userEmail') || 'user@example.com', isAdmin: data.data.isAdmin })
      } else {
        setUser({ email: localStorage.getItem('userEmail') || 'user@example.com' })
      }
    } catch (error) {
      setUser({ email: localStorage.getItem('userEmail') || 'user@example.com' })
    }
    
    // If there was a pending query, process it
    if (queryText || pendingQuery) {
      setPendingQuery(queryText || pendingQuery)
    }
  }

  const handleLogout = () => {
    setUser(null)
    localStorage.removeItem('authToken')
    localStorage.removeItem('userEmail')
  }

  if (user) {
    return (
      <>
        <InterstellarBackground />
        <Dashboard user={user} onLogout={handleLogout} initialQuery={pendingQuery} />
      </>
    )
  }

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden">
      <InterstellarBackground />

      {/* Glassmorphism Navigation */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 glass-card mx-auto mt-6 max-w-6xl px-8 py-4 flex items-center justify-between"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <span className="text-2xl font-black bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
            Agentcy1
          </span>
        </div>

        <div className="hidden md:flex items-center space-x-8">
          <a href="#features" className="text-white/80 hover:text-white transition-colors duration-300 font-medium">Features</a>
          <a href="#testimonials" className="text-white/80 hover:text-white transition-colors duration-300 font-medium">Testimonials</a>
          <a href="#statistics" className="text-white/80 hover:text-white transition-colors duration-300 font-medium">Statistics</a>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowAuthModal(true)}
            className="text-white/80 hover:text-white transition-colors duration-300 font-medium"
          >
            Sign In
          </button>
          <motion.button
            onClick={() => setShowAuthModal(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-full font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started
          </motion.button>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-32">
        <motion.div 
          className="max-w-5xl mx-auto text-center"
          style={{ y: heroY }}
        >
          <motion.h1 
            className="text-6xl md:text-8xl font-black mb-8 bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            AI Agents at Your
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Beck and Call
            </span>
          </motion.h1>

          <motion.p 
            className="text-xl md:text-2xl text-white/70 mb-12 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Deploy intelligent AI agents that work for you 24/7. Research, build, and deploy—all from your browser.
          </motion.p>

          {/* Psychedelic Input Pill */}
          <motion.form
            onSubmit={handleQuerySubmit}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="max-w-3xl mx-auto mb-12"
          >
            <div className="psychedelic-pill">
              <div className="flex items-center space-x-4">
                <Sparkles className="h-6 w-6 text-white/60 flex-shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="What would you like your AI agents to do?"
                  className="flex-1"
                />
                <motion.button
                  type="submit"
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-full font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300 flex-shrink-0"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ArrowRight className="h-5 w-5" />
                </motion.button>
              </div>
            </div>
          </motion.form>

          {/* Processing Progress Bar */}
          <AnimatePresence>
            {isProcessing && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="max-w-3xl mx-auto mb-8"
              >
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white/80 font-medium">Processing your request...</span>
                    <span className="text-purple-400 font-bold">{processingProgress}%</span>
                  </div>
                  <div className="progress-bar">
                    <motion.div
                      className="progress-bar-fill"
                      initial={{ width: 0 }}
                      animate={{ width: `${processingProgress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Browser Mockup */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="browser-mockup max-w-4xl mx-auto"
          >
            <div className="browser-header">
              <div className="browser-dots">
                <div className="browser-dot red" />
                <div className="browser-dot yellow" />
                <div className="browser-dot green" />
              </div>
              <div className="flex-1 text-center text-white/40 text-sm font-medium">
                agentcy.one/dashboard
              </div>
            </div>
            <div className="p-8 bg-gradient-to-br from-purple-900/20 to-pink-900/20 min-h-[400px] flex items-center justify-center">
              <div className="text-center">
                <Brain className="h-24 w-24 text-purple-400 mx-auto mb-6 animate-pulse" />
                <p className="text-2xl font-bold text-white mb-2">AI Agents Dashboard</p>
                <p className="text-white/60">Deploy and manage your AI workforce</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section - Bento Grid */}
      <section id="features" className="relative z-10 py-32 px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
            Powerful Features
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Everything you need to deploy and manage your AI agent workforce
          </p>
        </motion.div>

        <div className="bento-grid max-w-7xl mx-auto">
          <FeatureBox
            icon={Bot}
            title="Intelligent Agents"
            description="Deploy specialized AI agents that understand context and execute complex tasks autonomously"
            delay={0}
          />
          <FeatureBox
            icon={Globe}
            title="Browser-in-Place"
            description="Agents work directly in the browser, researching and interacting with websites like real employees"
            delay={0.1}
          />
          <FeatureBox
            icon={Network}
            title="Human-in-the-Loop"
            description="Review and approve agent actions, maintaining control over critical decisions"
            delay={0.2}
          />
          <FeatureBox
            icon={Zap}
            title="Token Economy"
            description="Fair usage system with token-based billing. Admin users get unlimited access"
            delay={0.3}
          />
          <FeatureBox
            icon={Shield}
            title="Safe & Secure"
            description="Built-in safety measures prevent illegal or nefarious tasks from being executed"
            delay={0.4}
          />
          <FeatureBox
            icon={Rocket}
            title="Instant Deployment"
            description="Deploy agents in seconds. No setup, no configuration, just results"
            delay={0.5}
          />
        </div>
      </section>

      {/* Statistics Section */}
      <section id="statistics" className="relative z-10 py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
              By the Numbers
            </h2>
            <p className="text-xl text-white/60">
              Join thousands of teams already using Agentcy1
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="glass-card p-12 text-center gleam"
            >
              <AnimatedCounter end={50000} duration={2} />
              <div className="text-white/60 text-xl mt-4">Active Users</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="glass-card p-12 text-center gleam"
            >
              <AnimatedCounter end={2500000} duration={2.5} />
              <div className="text-white/60 text-xl mt-4">Tasks Completed</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="glass-card p-12 text-center gleam"
            >
              <AnimatedCounter end={99} duration={1.5} />
              <span className="counter">%</span>
              <div className="text-white/60 text-xl mt-4">Satisfaction Rate</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="relative z-10 py-32 px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
            Loved by Teams
          </h2>
          <p className="text-xl text-white/60">
            See what our users are saying about Agentcy1
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          {[
            {
              text: "Agentcy1 transformed how we work. Our AI agents handle research, data analysis, and even deploy websites. It's like having a whole team working 24/7.",
              author: "Sarah Chen",
              role: "CEO, TechStart Inc"
            },
            {
              text: "The browser-in-place feature is game-changing. Watching agents navigate websites and complete tasks autonomously is incredible. Saves us hundreds of hours monthly.",
              author: "Marcus Rodriguez",
              role: "Operations Director"
            }
          ].map((testimonial, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: i * 0.2 }}
              viewport={{ once: true }}
              className="glass-card p-8 gleam"
            >
              <div className="flex mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-white/80 text-lg mb-6 leading-relaxed">{testimonial.text}</p>
              <div>
                <div className="font-bold text-white">{testimonial.author}</div>
                <div className="text-white/60 text-sm">{testimonial.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-32 px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center glass-card p-16 gleam"
        >
          <Sparkles className="h-20 w-20 text-purple-400 mx-auto mb-8" />
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
            Ready to Deploy Your AI Workforce?
          </h2>
          <p className="text-xl text-white/60 mb-10 max-w-2xl mx-auto">
            Join thousands of teams using Agentcy1 to automate tasks, research, and build—all powered by intelligent AI agents.
          </p>
          <motion.button
            onClick={() => setShowAuthModal(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xl px-12 py-5 rounded-full font-bold hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 inline-flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Rocket className="h-6 w-6" />
            <span>Get Started Free</span>
          </motion.button>
        </motion.div>
      </section>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        queryText={pendingQuery}
      />
    </div>
  )
}

export default App